import { Injectable, NgModule, OnDestroy } from '@angular/core';
import { RouterModule, RouterStateSnapshot, Routes, TitleStrategy } from '@angular/router';
import { AuthenticationGuard } from '@app/shared/guards/authentication.guard';
import { LayoutsEnum } from '@app/layout/layouts.enum';
import { ReferralGuard } from '@app/shared/guards/referral.guard';
import { Subject } from 'rxjs';
import { ComponentsOverviewComponent } from '@app/shared/components/components-overview/components-overview.component';
import { CompareComponent } from '@app/unauthenticated/compare/compare.component';
import { OtcDeskComponent } from '@app/unauthenticated/otc-desk/otc-desk.component';
import { AffiliateProgramComponent } from '@app/unauthenticated/affiliate-program/affiliate-program.component';
import { CurrencyDetailGuard } from '@app/shared/guards/currency-detail.guard';
import { MetaTagsService } from '@app/shared/services/meta-tags.service';
import { WebviewSessionComponent } from '@app/unauthenticated/webview-session/webview-session.component';

// every second route redirects /XYZ to the correct language version /(cs|en)/XYZ
export const routes: Routes = [
  {
    path: '',
    loadChildren: () => import('@app/unauthenticated/homepage/homepage.module').then((m) => m.HomepageModule),
    pathMatch: 'full',
  },
  {
    path: 'webview-session',
    component: WebviewSessionComponent,
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.webview },
  },
  {
    path: 'developers',
    loadChildren: () => import('./unauthenticated/developers/developers.module').then((m) => m.DevelopersModule),
  },
  {
    path: 'developers',
    redirectTo: '/developers',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'fees',
    loadChildren: () => import('./unauthenticated/fees/fees.module').then((m) => m.FeesModule),
  },
  {
    path: 'fees',
    redirectTo: '/fees',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'legal',
    loadChildren: () => import('./unauthenticated/legal/legal.module').then((m) => m.LegalModule),
  },
  {
    path: 'legal',
    redirectTo: '/legal',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'whistleblowing',
    loadChildren: () =>
      import('./unauthenticated/whistleblowing/whistleblowing.module').then((m) => m.WhistleblowingModule),
  },
  {
    path: 'whistleblowing',
    redirectTo: '/whistleblowing',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'bankid-error',
    loadChildren: () => import('./authentication/bankid-failed/bankid-failed.module').then((m) => m.BankidFailedModule),
  },
  {
    path: 'bankid-error',
    redirectTo: '/bankid-error',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.basic },
  },
  {
    path: 'prices',
    loadChildren: () => import('./unauthenticated/currencies/currencies.module').then((m) => m.CurrenciesModule),
  },
  {
    path: 'prices',
    redirectTo: '/prices',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'reset-password',
    loadChildren: () =>
      import('./authentication/reset-password/reset-password.module').then((m) => m.ResetPasswordModule),
  },
  {
    path: 'reset-password',
    redirectTo: 'reset-password',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.basic },
  },
  {
    path: 'login',
    loadChildren: () => import('./authentication/login/login.module').then((m) => m.LoginModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'login',
    redirectTo: 'login',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.basic },
  },
  {
    path: 'sign-up',
    loadChildren: () => import('./authentication/sign-up/sign-up.module').then((m) => m.SignUpModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'sign-up',
    redirectTo: 'sign-up',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.basic },
  },
  {
    path: 'sign-up/bankid-success',
    redirectTo: '/sign-up/bankid-success',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.basic },
  },
  {
    path: 'disable-2fa',
    loadChildren: () => import('./authentication/disable-2fa/disable-2fa.module').then((m) => m.DisableTwoFactorModule),
  },
  {
    path: 'disable-2fa',
    redirectTo: '/disable-2fa',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.basic },
  },
  {
    path: 'support',
    loadChildren: () => import('./unauthenticated/contact-form/contact-form.module').then((m) => m.ContactFormModule),
  },
  {
    path: 'support',
    redirectTo: 'support',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'faq',
    loadChildren: () => import('./unauthenticated/faq/faq.module').then((m) => m.FaqModule),
  },
  {
    path: 'faq',
    redirectTo: '/faq',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'our-story',
    loadChildren: () => import('./unauthenticated/our-story/our-story.module').then((m) => m.OurStoryModule),
  },
  {
    path: 'our-story',
    redirectTo: 'our-story',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'block-account',
    loadChildren: () =>
      import('./unauthenticated/block-account/block-account.module').then((m) => m.BlockAccountModule),
  },
  {
    path: 'block-account',
    redirectTo: 'block-account',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'close-account',
    loadChildren: () =>
      import('./unauthenticated/close-account/close-account.module').then((m) => m.CloseAccountModule),
  },
  {
    path: 'close-account',
    redirectTo: 'close-account',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'unsubscribe',
    loadChildren: () => import('./unauthenticated/unsubscribe/unsubscribe.module').then((m) => m.UnsubscribeModule),
  },
  {
    path: 'unsubscribe',
    redirectTo: 'unsubscribe',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: '404',
    loadChildren: () => import('./unauthenticated/404/404.module').then((m) => m.PageNotFoundModule),
  },
  {
    path: '404',
    redirectTo: '/404',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'error',
    loadChildren: () => import('./unauthenticated/error/error.module').then((m) => m.ErrorPageModule),
  },
  {
    path: 'error',
    redirectTo: '/error',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'affiliate',
    loadChildren: () => import('./authenticated/affiliate/affiliate.module').then((m) => m.AffiliateModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'affiliate',
    redirectTo: '/affiliate',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'customer-support',
    loadChildren: () => import('./authenticated/support/support.module').then((m) => m.SupportModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'customer-support',
    redirectTo: '/customer-support',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'referral',
    loadChildren: () => import('./authenticated/referrals/referrals.module').then((m) => m.ReferralsModule),
    canActivate: [ReferralGuard],
  },
  {
    path: 'referral',
    redirectTo: '/referral',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'quick-trade',
    loadChildren: () => import('./authenticated/quick-trade/quick-trade.module').then((m) => m.QuickTradeModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'quick-trade',
    redirectTo: '/quick-trade',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'quick-trade/bank-transfer',
    redirectTo: 'quick-trade/bank-transfer',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'quick-trade/checkout',
    redirectTo: 'quick-trade/checkout',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'quick-trade/:trade-direction/:currency-pair',
    redirectTo: '/quick-trade/:trade-direction/:currency-pair',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'recurring-buy',
    loadChildren: () => import('./authenticated/recurring-buy/recurring-buy.module').then((m) => m.RecurringBuyModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'recurring-buy',
    redirectTo: '/recurring-buy',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'verification',
    loadChildren: () => import('./authenticated/verification/verification.module').then((m) => m.VerificationModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'verification',
    redirectTo: '/verification',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'welcome',
    loadChildren: () => import('./authenticated/verification/welcome/welcome.module').then((m) => m.WelcomeModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'welcome',
    redirectTo: '/welcome',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'open-orders',
    loadChildren: () =>
      import('./authenticated/history/open-orders/open-orders.module').then((m) => m.OpenOrdersModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'open-orders',
    redirectTo: '/open-orders',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'order-history',
    loadChildren: () =>
      import('./authenticated/history/order-history/order-history.module').then((m) => m.OrderHistoryModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'order-history',
    redirectTo: '/order-history',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'transaction-history',
    loadChildren: () =>
      import('./authenticated/history/transaction-history/transaction-history.module').then(
        (m) => m.TransactionHistoryModule,
      ),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'transaction-history',
    redirectTo: '/transaction-history',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'components-overview',
    component: ComponentsOverviewComponent,
  },
  {
    path: 'components-overview',
    redirectTo: '/components-overview',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'price-alerts',
    loadChildren: () => import('./authenticated/price-alerts/price-alerts.module').then((m) => m.PriceAlertsModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'price-alerts',
    redirectTo: '/price-alerts',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'account-api',
    loadChildren: () => import('./authenticated/api/api.module').then((m) => m.ApiModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'account-api',
    redirectTo: '/account-api',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'account-log',
    loadChildren: () => import('@app/authenticated/log/account-log/account-log.module').then((m) => m.AccountLogModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'account-log',
    redirectTo: '/account-log',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'user-log',
    loadChildren: () => import('@app/authenticated/log/user-log/user-log.module').then((m) => m.UserLogModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'user-log',
    redirectTo: '/user-log',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'transfers',
    loadChildren: () =>
      import('./authenticated/deposits-withdrawals/currencies/currencies.module').then((m) => m.CurrenciesModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'transfers',
    redirectTo: '/transfers',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'deposit/fiat/:fiat',
    loadChildren: () =>
      import('./authenticated/deposits-withdrawals/deposit/fiat/deposit-fiat.module').then((m) => m.DepositFiatModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'deposit/fiat/:fiat',
    redirectTo: '/deposit/fiat/:fiat',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'deposit/crypto/:crypto',
    loadChildren: () =>
      import('./authenticated/deposits-withdrawals/deposit/crypto/deposit-crypto.module').then(
        (m) => m.DepositCryptoModule,
      ),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'deposit/crypto/:crypto',
    redirectTo: '/deposit/crypto/:crypto',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'withdraw/fiat/:fiat',
    loadChildren: () =>
      import('./authenticated/deposits-withdrawals/withdraw/fiat/withdraw-fiat.module').then(
        (m) => m.WithdrawFiatModule,
      ),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'withdraw/fiat/:fiat',
    redirectTo: '/withdraw/fiat/:fiat',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'withdraw/crypto/:crypto',
    loadChildren: () =>
      import('./authenticated/deposits-withdrawals/withdraw/crypto/withdraw-crypto.module').then(
        (m) => m.WithdrawCryptoModule,
      ),
    canActivate: [AuthenticationGuard],
    data: { layout: LayoutsEnum.secured },
  },
  {
    path: 'withdraw/crypto/:crypto',
    redirectTo: '/withdraw/crypto/:crypto',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'deposit/fiat',
    loadChildren: () =>
      import('./authenticated/deposits-withdrawals/deposit/fiat/deposit-fiat.module').then((m) => m.DepositFiatModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'deposit/fiat',
    redirectTo: '/deposit/fiat',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'account-profile',
    loadChildren: () => import('./authenticated/profile/profile.module').then((m) => m.ProfileModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'account-profile',
    redirectTo: '/account-profile',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'security',
    loadChildren: () => import('./authenticated/security/security.module').then((m) => m.SecurityModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'security',
    redirectTo: '/security',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'trade',
    loadChildren: () =>
      import('./authenticated/trading-platform/trading-platform.module').then((m) => m.TradingPlatformModule),
  },
  {
    path: 'trade',
    redirectTo: '/trade',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.tradingPlatform },
  },
  {
    path: 'portfolio',
    loadChildren: () => import('./authenticated/portfolio/portfolio.module').then((m) => m.PortfolioModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'portfolio',
    redirectTo: '/portfolio',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'portfolio/:account',
    redirectTo: '/portfolio/:account',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.secured },
  },
  {
    path: 'compare',
    component: CompareComponent,
    title: 'meta-tags.compare.title',
  },
  {
    path: 'compare',
    redirectTo: '/compare',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'affiliate-program',
    component: AffiliateProgramComponent,
    title: 'meta-tags.affiliate-program.title',
  },
  {
    path: 'affiliate-program',
    redirectTo: '/affiliate-program',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'otc-desk',
    component: OtcDeskComponent,
    title: 'meta-tags.otc-desk.title',
  },
  {
    path: 'otc-desk',
    redirectTo: '/otc-desk',
    data: { skipRouteLocalization: { localizeRedirectTo: true }, layout: LayoutsEnum.default },
  },
  {
    path: 'price/:currency',
    loadChildren: () =>
      import('./unauthenticated/currency-detail/currency-detail.module').then((m) => m.CurrencyDetailModule),
    pathMatch: 'full',
    canActivate: [CurrencyDetailGuard],
  },
  {
    path: 'price/:currency',
    redirectTo: '/price/:currency',
    data: {
      skipRouteLocalization: { localizeRedirectTo: true },
      layout: LayoutsEnum.default,
    },
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];

@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy implements OnDestroy {
  private unsubscribe$ = new Subject<void>();

  constructor(private metaTagsService: MetaTagsService) {
    super();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  override updateTitle(routerState: RouterStateSnapshot) {
    const routeData = this.getRouteData(routerState);
    // Skip title update if explicitly specified in route data
    // This allows child routes to handle their own title updates
    // For example, the currency detail page manages its own meta tags
    if (routeData?.skipTitleUpdate) {
      return;
    }

    const titleKey = this.buildTitle(routerState);
    this.metaTagsService.setTitleAndMetaTags(titleKey || null);
  }

  private getRouteData(routerState: RouterStateSnapshot) {
    let route = routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.data || route.parent?.data;
  }
}

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
    }),
  ],
  providers: [
    {
      provide: TitleStrategy,
      useClass: AppTitleStrategy,
    },
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
